import * as React from 'react'
import { AlertTriangle, Inbox, Loader2, RefreshCw, Clock } from 'lucide-react'
import { ApiError } from '@/lib/api'
import { Button } from './button'
import { Skeleton } from './misc'
import { cn } from '@/lib/utils'

/**
 * §5.8: every page must render one of Loading / Empty / Error / Partial /
 * Stale. A blank page is never acceptable, so these are the only five shapes
 * a list is allowed to be in.
 */

export function LoadingState({ rows = 5, label = '加载中' }: { rows?: number; label?: string }) {
  return (
    <div className="space-y-2" role="status" aria-label={label}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-11 w-full" />
      ))}
      <span className="sr-only">{label}</span>
    </div>
  )
}

export function InlineSpinner({ label = '处理中' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-muted-foreground" role="status">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {label}
    </span>
  )
}

export function EmptyState({
  title,
  description,
  action,
  icon: Icon = Inbox,
}: {
  title: string
  description?: string
  action?: React.ReactNode
  icon?: React.ComponentType<{ className?: string }>
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed px-6 py-14 text-center">
      <Icon className="mb-3 h-9 w-9 text-muted-foreground/60" aria-hidden />
      <p className="text-sm font-medium">{title}</p>
      {description && <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const apiError = error instanceof ApiError ? error : null
  const message = error instanceof Error ? error.message : '发生未知错误'

  return (
    <div
      role="alert"
      className="rounded-lg border border-destructive/40 bg-destructive/5 p-5"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-destructive">{message}</p>
          {apiError?.fieldErrors.length ? (
            <ul className="mt-2 space-y-1 text-xs text-destructive/90">
              {apiError.fieldErrors.map((f) => (
                <li key={f.path}>
                  <code className="rounded bg-destructive/10 px-1">{f.path}</code> {f.message}
                </li>
              ))}
            </ul>
          ) : null}
          {apiError?.traceId && (
            <p className="mt-2 font-mono text-[11px] text-muted-foreground">
              trace_id: {apiError.traceId}
            </p>
          )}
          {onRetry && (apiError?.retryable ?? true) && (
            <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
              <RefreshCw className="h-3.5 w-3.5" aria-hidden /> 重试
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Partial state: the request succeeded but some data is missing or degraded.
 * §5.8 lists it separately from Error because the page is still usable.
 */
export function PartialState({ message, className }: { message: string; className?: string }) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:text-amber-300',
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{message}</span>
    </div>
  )
}

/**
 * Stale state: the data shown was confirmed too long ago to be relied upon.
 * §7.1 requires the source and confirmation time to travel with any status,
 * so this banner always states when the value was last confirmed.
 */
export function StaleState({ confirmedAt, className }: { confirmedAt?: string; className?: string }) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-2 rounded-md border border-muted-foreground/30 bg-muted/50 px-3 py-2 text-xs text-muted-foreground',
        className,
      )}
    >
      <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>
        当前展示的状态确认于 {confirmedAt ?? '未知时间'}，可能已过期。
        可注册性与释放时间以注册商结果为准。
      </span>
    </div>
  )
}
